// services/invoiceApi.ts
import apiClient from "./apiClient";

export interface InvoiceSummary {
  id: number;
  invoiceNo: string;
  customerId: number;
  organizationId: number;
  invoiceDate: string;
  totalAmount: string; // decimal string
  discount: string;
  taxAmount: string;
  grandTotal: string;
  status: "draft" | "confirmed";
  printed: boolean;
  createdAt: string;
  Customer: {
    id: number;
    fullName: string;
    phone: string;
    balance: string;
  };
}

export interface InvoiceLine {
  id: number;
  invoiceId: number;
  productId: number;
  quantity: number;
  unitPrice: string;
  discountPercent: string;
  taxPercent: string;
  lineTotal: string;
  Product?: {
    id: number;
    name: string;
    sku: string;
    packQuantity: number;
    Unit?: { symbol: string };
  };
}

export interface ReturnRequest {
  id: number;
  invoiceId: number;
  productId: number;
  quantity: number;
  reason: string;
  status: "pending" | "approved" | "rejected";
  createdAt: string;
  Product: {
    name: string;
  };
}

export interface InvoiceDetail {
  id: number;
  invoiceNo: string;
  customerId: number;
  organizationId: number;
  invoiceDate: string;
  totalAmount: string;
  discount: string;
  taxAmount: string;
  grandTotal: string;
  status: "draft" | "confirmed";
  printed: boolean;
  createdAt: string;
  Customer: {
    id: number;
    fullName: string;
    phone: string;
    balance: string;
    areaId: number;
  };
  InvoiceLines: InvoiceLine[];
  ReturnRequests: ReturnRequest[];
}

export interface PaginatedInvoices {
  invoices: InvoiceSummary[];
  pagination: {
    totalItems: number;
    totalPages: number;
    currentPage: number;
    itemsPerPage: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

// ... payload types unchanged
export interface CreateDraftPayload {
  /* as before */
}
export interface CreateDraftResponse {
  /* as before */
}
export interface PaymentPayload {
  /* as before */
}

export interface ReturnRequestItem {
  productId: number;
  quantity: number;
  reason?: string;
}

export interface ReturnRequestPayload {
  invoiceId: number;
  items?: ReturnRequestItem[];
  fullInvoiceReturn?: boolean;
  reason?: string; // common reason (applies to all items if not overridden)
  // Legacy single item (keep for backward compatibility)
  productId?: number;
  quantity?: number;
}

export interface ReturnRequestResponse {
  message: string;
  createdRequests?: any[];
  errors?: string[];
}

export const invoiceApi = {
  createDraft: async (
    data: CreateDraftPayload,
  ): Promise<CreateDraftResponse> => {
    const response = await apiClient.post("/invoices", data);
    return response.data;
  },
  confirmInvoice: async (invoiceId: number): Promise<{ message: string }> => {
    const response = await apiClient.put(`/invoices/confirm/${invoiceId}`);
    return response.data;
  },
  listInvoices: async (params: {
    startDate?: string;
    endDate?: string;
    customerId?: number;
    createdBy?: number;
    areaId?: number;
    page?: number;
    limit?: number;
  }): Promise<PaginatedInvoices> => {
    const response = await apiClient.get("/invoices", { params });
    return response.data;
  },
  getInvoiceById: async (id: number): Promise<InvoiceDetail> => {
    const response = await apiClient.get(`/invoices/${id}`);
    // response.data has { invoice: { ... } }
    return response.data.invoice;
  },
  recordPayment: async (data: PaymentPayload): Promise<{ message: string }> => {
    const response = await apiClient.post("/invoices/payment", data);
    return response.data;
  },
  requestReturn: async (
    data: ReturnRequestPayload,
  ): Promise<ReturnRequestResponse> => {
    const response = await apiClient.post("/invoices/return-request", data);
    return response.data;
  },
  approveReturn: async (
    returnRequestId: number,
  ): Promise<{ message: string }> => {
    const response = await apiClient.put(
      `/invoices/return-approve/${returnRequestId}`,
    );
    return response.data;
  },
  rejectReturn: async (
    returnRequestId: number,
    rejectionReason?: string,
  ): Promise<{ message: string }> => {
    const response = await apiClient.put(
      `/invoices/return-reject/${returnRequestId}`,
      { rejectionReason },
    );
    return response.data;
  },
  printInvoice: async (invoiceId: number): Promise<Blob> => {
    const response = await apiClient.get(`/invoices/print/${invoiceId}`, {
      responseType: "blob",
    });
    return response.data;
  },
  // services/invoiceApi.ts – add this method inside invoiceApi object
  bulkPdf: async (params: {
    startDate?: string;
    endDate?: string;
    today?: boolean;
    createdBy?: number;
    customerId?: number;
    areaId?: number; // ✅ new
  }): Promise<Blob> => {
    const response = await apiClient.get("/invoices/bulk-pdf", {
      params,
      responseType: "blob",
    });
    return response.data;
  },
  updateInvoice: async (id: number, data: any) => {
    const response = await apiClient.put(`/invoices/${id}`, data);
    return response;
  },

  revertToDraft: async (id: number) => {
    const response = await apiClient.post(`/invoices/${id}/revert-to-draft`);
    return response;
  },
  bulkApproveReturns: async (data: {
    invoiceId?: number;
    approveAll?: boolean;
    returnRequestIds?: number[];
  }): Promise<{ message: string }> => {
    const response = await apiClient.post(
      "/invoices/return-approve/bulk",
      data,
    );
    return response.data;
  },
};
